/**
 * ScoringUtils.js
 * Module de scoring partagé entre la version desktop et mobile
 * Centralise toute la logique de calcul de score pour éviter la duplication
 */

import { getCertaintyCoeff, getValidationThreshold } from '../config/scoringConfig';
import { getQcmCanonicalMetadata } from './qcmMetadata';

/**
 * Calcule le score unitaire pour une option
 * @param {boolean} isCorrect - L'option est-elle correcte ?
 * @param {string} certainty - Degré de certitude ('high', 'medium', 'low')
 * @param {number} elapsedTime - Temps écoulé en secondes
 * @param {number} baseScore - Score de base (par défaut 2)
 * @param {number} maxTime - Temps maximum en secondes (par défaut 60)
 * @returns {Object} - {score, breakdown}
 */
export const calculateUnitaryScore = (
    isCorrect, 
    certainty, 
    elapsedTime, 
    baseScore = 2, 
    maxTime = 60
) => {
    const certaintyCoeff = getCertaintyCoeff(certainty);

    const speedPercentage = Math.min(100, (elapsedTime / maxTime) * 100);
    let speedCoeff = 1.0;
    if (speedPercentage > 75) {
        speedCoeff = 0.95;
    } else if (speedPercentage > 50) {
        speedCoeff = 0.98;
    }

    let score = baseScore * speedCoeff;
    
    // Si incorrect, score négatif
    if (!isCorrect) {
        score = -score;
    }

    return {
        score: parseFloat(score.toFixed(1)),
        breakdown: {
            baseScore,
            certaintyCoeff,
            speedCoeff,
            speedPercentage: Math.round(speedPercentage),
            isCorrect
        }
    };
};

/**
 * Calcule le score total d'un QCM
 * @param {Array} optionScores - Tableau des scores par option
 * @returns {Object} - {score, maxScore, percentage}
 */
export const calculateTotalScore = (optionScores) => {
    const totalScore = optionScores.reduce((sum, opt) => sum + opt.unitaryScore, 0);
    const maxScore = optionScores.length * 2; // Chaque option vaut max 2 points
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;

    return {
        score: parseFloat(totalScore.toFixed(1)),
        maxScore,
        percentage: Math.round(percentage)
    };
};

/**
 * Calcule l'indicateur de lenteur
 * @param {Array} optionScores - Tableau des scores par option avec elapsed_time
 * @param {number} maxTime - Temps maximum par option (par défaut 60s)
 * @returns {Object} - {slowness, slownessText}
 */
export const calculateSlowness = (optionScores, maxTime = 60) => {
    if (optionScores.length === 0) {
        return { slowness: 0, slownessText: 'N/A' };
    }

    const avgTime = optionScores.reduce((sum, opt) => sum + opt.elapsed_time, 0) / optionScores.length;
    const slowness = Math.min(100, Math.round((avgTime / maxTime) * 100));

    let slownessText = 'Fast';
    if (slowness > 75) {
        slownessText = 'Slow';
    } else if (slowness > 50) {
        slownessText = 'Normal';
    }

    return { slowness, slownessText };
};

/**
 * Extrait les faiblesses (weaknesses) basées sur les erreurs
 * @param {Array} optionScores - Tableau des scores par option
 * @param {Object} currentQCM - QCM actuel avec metadata
 * @returns {Array} - Liste des faiblesses uniques
 */
export const extractWeaknesses = (optionScores, currentQCM) => {
    const weaknesses = [];

    optionScores.forEach(opt => {
        if (!opt.is_correct) {
            // Ajouter les keywords de l'option incorrecte
            if (opt.keywords && Array.isArray(opt.keywords)) {
                weaknesses.push(...opt.keywords);
            }
            // Ajouter le domaine si présent
            if (opt.domain) {
                weaknesses.push(opt.domain);
            }
            // Ajouter la tâche si présente
            if (opt.task) {
                weaknesses.push(opt.task);
            }
        }
    });

    // Retourner les faiblesses uniques
    return [...new Set(weaknesses)];
};

/**
 * Compte les certitudes par type (correct/incorrect)
 * @param {Array} optionScores - Tableau des scores par option
 * @returns {Object} - Compteurs de certitude
 */
export const countCertainties = (optionScores) => {
    const counts = {
        correct: { high: 0, medium: 0, low: 0 },
        incorrect: { high: 0, medium: 0, low: 0 }
    };

    optionScores.forEach(opt => {
        const category = opt.is_correct ? 'correct' : 'incorrect';
        const cert = opt.certainty || 'medium';
        if (counts[category][cert] !== undefined) {
            counts[category][cert]++;
        }
    });

    return counts;
};

/**
 * QCM validé si score sur 10 >= seuil global (scoringConfig.validation.qcm_validation_threshold).
 */
export const isQCMValidated = (totalScore, maxScore) => {
    const scoreOn10 = maxScore > 0 ? (totalScore / maxScore) * 10 : 0;
    return scoreOn10 >= getValidationThreshold() ? 1 : 0;
};

/**
 * Formate les données de score pour l'affichage et l'envoi au backend
 * @param {Array} optionScoresArray - Tableau des scores par option
 * @param {Object} currentQCM - QCM actuel
 * @param {number} attemptCount - Nombre de tentatives
 * @returns {Object} - Données de score formatées
 */
export const formatScoreData = (optionScoresArray, currentQCM, attemptCount = 1) => {
    const totalScoreData = calculateTotalScore(optionScoresArray);
    const slownessData = calculateSlowness(optionScoresArray);
    const uniqueWeaknesses = extractWeaknesses(optionScoresArray, currentQCM);
    const certaintyCounts = countCertainties(optionScoresArray);
    const qcmIsValid = isQCMValidated(totalScoreData.score, totalScoreData.maxScore);

    return {
        score: totalScoreData.score,
        maxScore: totalScoreData.maxScore,
        percentage: totalScoreData.percentage,
        slowness: slownessData.slowness,
        slownessText: slownessData.slownessText,
        attempts: attemptCount,
        certaintyCounts,
        uniqueWeaknesses,
        qcmIsValid
    };
};

/**
 * Calcule les statistiques d'affichage pour la section solution
 * @param {Object} scoreData - Données de score formatées
 * @returns {Object} - Statistiques pour l'UI
 */
export const calculateDisplayStats = (scoreData) => {
    const scoreOn10 = scoreData.maxScore > 0 
        ? ((scoreData.score / scoreData.maxScore) * 10).toFixed(1) 
        : '0.0';

    // Déterminer la couleur du score
    let scoreColor = 'text-red-400';
    if (parseFloat(scoreOn10) >= getValidationThreshold()) {
        scoreColor = 'text-green-400';
    } else if (parseFloat(scoreOn10) >= 6) {
        scoreColor = 'text-yellow-400';
    }

    // Déterminer la couleur de la lenteur
    let slownessColor = 'text-green-400';
    if (scoreData.slowness > 75) {
        slownessColor = 'text-red-400';
    } else if (scoreData.slowness > 50) {
        slownessColor = 'text-yellow-400';
    }

    return {
        scoreOn10,
        scoreColor,
        slownessColor,
        isValidated: scoreData.qcmIsValid === 1,
        hasWeaknesses: scoreData.uniqueWeaknesses.length > 0
    };
};

/**
 * Prépare le payload pour l'envoi au backend (qcm_multi_response_score)
 * @param {Object} params - Paramètres nécessaires
 * @returns {Object} - Payload formaté
 */
export const prepareBackendPayload = ({
    sessionContext,
    currentQCM,
    optionScoresArray,
    scoreData,
    qcmType = 'qcm'
}) => {
    const qcmId = currentQCM?.qcm_id || '';
    const { domain, task, topic, keywords } = getQcmCanonicalMetadata(currentQCM);

    return {
        user_id: sessionContext?.user || 'anonymous_user',
        exam_id: sessionContext?.exam || 'default_exam',
        session_id: sessionContext?.sessionId || Date.now().toString(),
        qcm_id: qcmId,
        questionType: qcmType,
        domain: domain,
        task: task,
        topic: topic,
        keywords: keywords,
        timestamp: new Date().toISOString(),
        total_elapsed_time: Math.max(...optionScoresArray.map(opt => opt.elapsed_time), 0),
        total_score: scoreData.score,
        max_score: scoreData.maxScore,
        option_scores: optionScoresArray.map(opt => ({
            option: opt.option,
            unitary_score: opt.unitaryScore,
            is_correct: opt.is_correct,
            certainty: opt.certainty,
            elapsed_time: opt.elapsed_time,
            domain: opt.domain,
            task: opt.task,
            topic: opt.topic,
            keywords: opt.keywords,
            qcm_id: opt.qcm_id,
            score_breakdown: opt.scoreBreakdown
        })),
        summary_stats: {
            slowness: scoreData.slowness,
            slowness_text: scoreData.slownessText,
            attempts: scoreData.attempts,
            certainty_counts: scoreData.certaintyCounts,
            weaknesses: scoreData.uniqueWeaknesses,
            qcm_is_valid: scoreData.qcmIsValid
        }
    };
};

export default {
    calculateUnitaryScore,
    calculateTotalScore,
    calculateSlowness,
    extractWeaknesses,
    countCertainties,
    isQCMValidated,
    formatScoreData,
    calculateDisplayStats,
    prepareBackendPayload
};


